import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { Modal as SemanticModal, Transition } from "semantic-ui-react";

import Button from "@cayuse/shared-react.components.button";
import Radio from "@cayuse/shared-react.components.form.inputs.radio";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Modal from "@cayuse/shared-react.components.modal";
import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import SearchBar from "components/SearchBar";
import {
	COLUMN_HEADER_KEYS,
	ERROR_NOT_AUTHORIZED,
	ERROR_STATUS_CODE_400,
	IS_DEV,
	PRIVILEGES,
	PROPOSALS_BASE_ROUTE,
	START_PROPOSAL_FROM_PROJECT,
} from "globals/constants";
import { useDictionary, useHasPrivileges } from "hooks";
import { findProjects, refreshProjects } from "store/actions/projectList";
import { refreshProposals } from "store/actions/proposals";
import { fetchShell } from "store/actions/shell";
import { createFromOptions, debounce, notifyError, notifySuccess, spUrl, validateTitleLength } from "utils";
import { projectListSelectors, tenantSelectors } from "store/selectors";
import history from "utils/history";

import styles from "./styles.css";
import { api } from "services";


const StartNewProposal = () => {
	const actions = {
		create: "create",
		update: "update"
	};

	const defaults = {
		action: undefined,
		visible: false,
		open: false,
		transitionDuration: 150,
		titleField: "",
		searchField: undefined,
		searchInput: "",
		confirmDisabled: true,
		searchQuery: ""
	};

	const dictionary = useDictionary("proposals.startNewProposal");
	const MODAL_ID_BASE = "newProposalModal";

	const [ isLoading, setIsLoading ] = useState(false);
	const [ action, setAction ] = useState(defaults.action);
	const [ visible, setVisible ] = useState(defaults.visible);
	const [ open, setOpen ] = useState(defaults.open);
	const [ titleField, setTitleField ] = useState(defaults.titleField);
	const [ searchField, setSearchField ] = useState(defaults.searchField);
	const [ confirmDisabled, setConfirmDisabled ] = useState(defaults.confirmDisabled);
	const [ disabledReason, setDisabledReason ] = useState(null);
	const [ projectOptions, setProjectOptions ] = useState([]);
	const [ searchInput, setSearchInput ] = useState(defaults.searchInput);

	const { items } = useSelector(projectListSelectors.content);
	const tenantId = useSelector(tenantSelectors.tenantId);

	const ariaControlsAttr = open ? { [`aria-controls`]: MODAL_ID_BASE } : {};

	const {
		[PRIVILEGES.PROPOSAL_CREATE]: canCreateProposals
	} = useHasPrivileges([ PRIVILEGES.PROPOSAL_CREATE ]);

	useEffect(() => {
		const errors = dictionary.errors || {};
		let reason = null;

		if (confirmDisabled) {
			if (!action) {
				reason = errors.noActionSelected || "Please select where to create a new proposal";
			} else if (action === actions.create) {
				reason = errors.noProjectTitle || "Please enter a project title";
			} else {
				reason = errors.noProjectSelected || "Please select a project";
			}
		}

		if (reason !== disabledReason) {
			setDisabledReason(reason);
		}
	}, [ action, actions.create, confirmDisabled, dictionary.errors, disabledReason, searchField, titleField ]);
	const hasProjectOptions = isNonEmptyArray(items);

	const refreshData = () => {
		fetchShell();
		refreshProposals();
		refreshProjects();
	};

	const onActionChange = value => {
		setAction(value);

		if (value === actions.create) {
			setConfirmDisabled(!titleField || !isNonEmptyString(titleField.trim()) || !validateTitleLength(titleField));
		} else if (value === actions.update) {
			setConfirmDisabled(!searchField || !isNonEmptyString(searchField.trim()));
		}

		if (!visible) {
			setVisible(true);
		}
	};

	const onOpen = () => {
		if(!hasProjectOptions) {
			findProjects({ pageLimit: 1, pageOffset: 0, searchTerms: {} });
		}
		setAction(defaults.action);
		setVisible(defaults.visible);
		setTitleField(defaults.titleField);
		setSearchField(defaults.searchField);
		setSearchInput(defaults.searchInput);
		setOpen(true);
	};

	const onClose = () => {
		setOpen(false);

		const triggerButton = document.getElementById("startNewProposalBtn");

		if (triggerButton) {
			triggerButton.focus();
		}
	};

	const onConfirmCreate = () => {
		onSave({ projectTitle: titleField }, "createProposalAndProject");
	};

	const onConfirmUpdate = () => {
		onSave({ projectId: searchField }, "createProposal");
	};

	const onSave = async (payload, actionType) => {
		try {
			const response = await api.proposal.create(payload, { notifyUponError: false });
			const proposal = response.submission;

			refreshData();
			onClose();
			notifySuccess(dictionary.successMessages[`${actionType}`]);
			history.push(spUrl(`${PROPOSALS_BASE_ROUTE}/${proposal.proposalId}`));
		} catch (error) {
			setIsLoading(false);

			if (error.response.status === ERROR_STATUS_CODE_400) {
				notifyError(dictionary.errors.notAuthorized || ERROR_NOT_AUTHORIZED, error);
			} else {
				const message = IS_DEV ? error.message : dictionary.errors.createProjectFailed;
				notifyError(message, error);
			}
		}
	};

	const onTitleChange = event => {
		const projectTitle = event.target.value;
		setConfirmDisabled(!projectTitle || !isNonEmptyString(projectTitle.trim()) || !validateTitleLength(projectTitle));
		setTitleField(projectTitle);
	};

	const onSearchChange = debounce(searchQuery => {
		setSearchInput(searchQuery);
	}, 750);

	const addToManyResultsText = (options, totalElements, searchInput) => {
		if(options?.length < totalElements) {
			return {
				key: "refine-results",
				value: "refine-results",
				text: `There are too many results for "${searchInput}", try adding to your search to see more specific results`,
				disabled: true
			};
		}

		return false;
	};

	useEffect(() => {
		setProjectOptions([]);
		if(isNonEmptyString(searchInput) && action === actions.update) {
			createFromOptions({
				fields: START_PROPOSAL_FROM_PROJECT.requiredFields,
				searchFields: START_PROPOSAL_FROM_PROJECT.searchFields,
				searchQuery: searchInput,
				formType: COLUMN_HEADER_KEYS.PROJECT.toLowerCase()
			})
				.then(results => {
					const updatedOptions = results?.content?.map((proposalItem, index) => {
						const textString = `${proposalItem.projectNumber} : ${proposalItem.title}`;

						if(isNonEmptyString(proposalItem.projectNumber) && isNonEmptyString(proposalItem.title)) {
							return {
								content: textString,
								key: `${proposalItem.projectId}`,
								value: proposalItem.projectId,
								original: textString,
								text: `Result ${index + 1} of ${results?.content?.length} for ${searchInput}: ${textString}`,
								projectTitle: proposalItem.title,
							};
						}

						return { key: "undefined" };
					});

					const toManyResultsText = addToManyResultsText(updatedOptions, results?.totalElements, searchInput);

					if(toManyResultsText) {
						updatedOptions.push(toManyResultsText);
					}

					// Remove duplicate projects
					const ids = updatedOptions.map(option => option.key);
					const filteredOptions = updatedOptions.filter(({ key }, index) => (
						!ids.includes(key, index + 1) && key !== "undefined"
					));

					setProjectOptions(filteredOptions);
				})
				.finally(() => setIsLoading(false));
		}
	}, [ action, actions.update, searchInput, tenantId ]);

	const handleOnChange = (projectId, options) => {
		// Used to change the display text of the selected item
		const updateDisplayText = () => {
			// find the selected option and restore the original value
			// const selected = _find(options, { value: value });
			const selected = options.find(option => option.key === projectId);

			if(isNonEmptyObject(selected)) {
				selected.text = selected.original;
			}

			const updatedOptions = options.map(option => option.key === selected.key ? selected : option);

			// now set only one option in the list of results
			setProjectOptions(updatedOptions);
		};

		if(isNonEmptyString(projectId)) {
			updateDisplayText();
			setSearchField(projectId);
			setConfirmDisabled(false);
		} else {
			setSearchInput(defaults.searchInput);
			setSearchField(defaults.searchField);
			setConfirmDisabled(defaults.confirmDisabled);
		}
	};

	const onConfirm = () => {
		setIsLoading(true);
		if (action === actions.create) {
			onConfirmCreate();
		} else if (action === actions.update) {
			onConfirmUpdate();
		} else {
			console.error("Not implemented: ", action);
		}
	};

	useEffect(() => {
		if (visible) {
			ReactTooltip.rebuild();
		}
	}, [ visible ]);

	return (
		<Modal
			id={MODAL_ID_BASE}
			ariaLabel={dictionary.createNew || "Create New Proposal"}
			className={styles.modal}
			closeIcon
			trigger={(
				<IconButton
					icon="plus"
					labelPosition="left"
					primary
					disabled={!canCreateProposals}
					{...ariaControlsAttr}
					onClick={onOpen}
					id="startNewProposalBtn"
					aria-haspopup="dialog"
				>
					{dictionary.startNew || "Start New Proposal"}
				</IconButton>
			)}
			open={open}
			onClose={onClose}
			size="one-half"
		>
			<SemanticModal.Header className={`ui header ${styles.modalHeading} whiteOnBlue`}>
				<h2>{dictionary.createNew || "Create New Proposal"}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				<div className={`ui form ${styles.newProposalForm}`}>
					<Radio
						id={actions.create}
						name="actionGroup"
						checked={action === actions.create}
						value={actions.create}
						onChange={() => onActionChange(actions.create)}
					>
						<span
							dangerouslySetInnerHTML={{
								__html: dictionary.newBlurb || "This proposal <i>is not</i> related to any existing proposals or awards (Create New Project)"
							}}
						/>
					</Radio>

					{visible && action === actions.create &&
						<div className={styles.dropdownWrapper}>
							<label htmlFor="newProjectTitleInput" className={styles.label}>
								{dictionary.newLabel || "Enter a title for your project"}
							</label>
							<input
								id="newProjectTitleInput"
								className={styles.modalInput}
								placeholder={dictionary.searchPlaceholder || "New project title"}
								type="text"
								onChange={onTitleChange}
								value={titleField}
							/>
							{!validateTitleLength(titleField) && <span className={styles.titleLengthWarning}>
								{dictionary.errors.projectTitleLengthValidation ?? "Project title must be less than 256 characters."}
							</span>}
						</div>
					}

					<Radio
						id={actions.update}
						name="actionGroup"
						checked={action === actions.update}
						value={actions.update}
						onChange={() => onActionChange(actions.update)}
						disabled={!hasProjectOptions}
					>
						<span
							dangerouslySetInnerHTML={{
								__html: dictionary.searchBlurb || "This proposal <i>is</i> related to existing proposals or awards (Add Proposal to Project)"
							}}
						/>
					</Radio>

					{visible && action === actions.update &&
						<div className={styles.dropdownWrapper}>
							<label htmlFor="existingProjectTitleSearch" className={styles.label}>
								{dictionary.searchLabel || "Click to search by Project Number or Project Title"}
							</label>

							<SearchBar
								id="existingProjectTitleSearch"
								className={styles.modalInput}
								options={projectOptions}
								onChange={(event, { value }) => handleOnChange(value, projectOptions)}
								onSearchChange={(event, { searchQuery }) => onSearchChange(searchQuery)}
								value={searchField}
							/>
						</div>
					}
				</div>
			</SemanticModal.Content>
			{!visible ? null :
				<SemanticModal.Actions>
					<Transition visible={visible} animation="scale right" duration={defaults.transitionDuration}>
						<div className="field">
							<Button secondary onClick={onClose}>{dictionary.cancel || "Cancel"}</Button>
							<Button
								className={styles.confirmButton}
								onClick={onConfirm}
								disabled={confirmDisabled || isLoading}
								busy={isLoading}
								loadingMessage="Creating proposal"
								tooltip={confirmDisabled && disabledReason}
							>
								{action === actions.create
									? dictionary.confirmNewProject || "Create New Project"
									: dictionary.confirmNewProposal || "Add New Proposal to Project"
								}
							</Button>
						</div>
					</Transition>
				</SemanticModal.Actions>
			}
		</Modal>
	);
};

export default StartNewProposal;
